import { A } from "@/atoms-react/a/A.react";
import Avatar from "@/atoms-react/avatar/Avatar.react";
import { Button } from "@/atoms-react/button/Button.react";
import Column from "@/atoms-react/column/Column.react";
import InputLabel from "@/atoms-react/input/Input.react";
import type { LayoutBaseProps } from "@/atoms-react/layout-base/LayoutBase.react";
import { Base } from "@/atoms-react/text/Text.react";
import config from "@/config/index.ts";
import { useAuth } from "@/features/auth/hooks/use-auth-react";
import useCheckEmailExistsMutation from "@/features/flow/hooks/use-check-email-exists";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import { FromURL } from "@/features/flow/types";
import { usePartnerInfo } from "@/features/partner/hooks/use-partner-information";
import { typeElement } from "@/features/partner/types";
import { convertToDesign } from "@/features/partner/utils";
import { validateEmailFormat } from "@/features/utils/functions";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { FlowHeader } from "../../flow-header.react";
import { useAppSelector } from "@/redux/hooks";
import { getMirrorUuid } from "@/redux/slices/signupSlice";
import useCaptchaVerify from "@/features/utils/hooks/use_captcha_verify";
interface AccountEmailSelectProps extends Omit<LayoutBaseProps, "center"> {
  onValidated?: (output: AccountWelcomeOutput) => void;
  overlayMode?: boolean;
}

export interface AccountWelcomeOutput {
  emailExists: boolean;
  email: string;
  is2faActivated: boolean;
  existAsAlias: boolean;
  mirrorExist: boolean;
  real_uuid: string | null;
  realExist: boolean;
  existStatus: boolean;
}

function AccountWelcome({ onValidated = () => {} }: AccountEmailSelectProps) {
  const { getLanguage } = usei18n();
  const { partnerLoginDataQuery } = usePartnerInfo();
  const mirorIdentity = useAppSelector(getMirrorUuid);
  const partner = (() => {
    const originUrl = document.location.origin;
    const clientURl = new URL(originUrl);
    const subdomain = clientURl.hostname.split(".")[0];
    if (!["pass", "newpass", "app"].includes(subdomain)) {
      return subdomain;
    }
    return sessionStorage.getItem("origin");
  })();
  const { data } = partnerLoginDataQuery(partner, "login");
  const [subDescription] = useState<string>("");
  const origin = useOrigin();
  const { t } = useTranslation(["pass"]);
  const language = getLanguage();

  const { cached, removeCached } = useAuth();

  const { mutate: checkEmailExists, isPending } = useCheckEmailExistsMutation({
    onError: (error) => toast.error(error.message)
  });
  const [email, setEmail] = useState<string>("");
  const getFooter = useMemo(() => {
    const text = convertToDesign(data, typeElement.FOOTER_INFO);
    return getLanguage() === "fr" ? text?.content_fr : text?.content_en;
  }, [data]);
  const { isReady } = useCaptchaVerify();
  const execute = () => {
    sessionStorage.removeItem("NewOlkyPayUser");
    if (!email) {
      toast.error(t("flow.id_control.error.email_required"));
      return;
    }

    const validEmail = validateEmailFormat(email);
    if (!validEmail) {
      toast.error(t("flow.id_control.error.email_invalid"));
      return;
    }
    if (!isReady) {
      toast.error(t("pass:flow.id_control.error.captcha_not_available_yet"));
      return;
    }
    checkEmailExists(
      { email: email, uuid: mirorIdentity ?? undefined },
      {
        onSuccess: (data) => {
          const { existAsAlias, realExist, is2faActivated, mirrorExist, real_uuid, existStatus } =
            data;
          onValidated({
            email,
            emailExists: existAsAlias ?? false,
            is2faActivated: is2faActivated ?? false,
            existAsAlias: existAsAlias ?? false,
            mirrorExist: mirrorExist ?? false,
            real_uuid: real_uuid ?? null,
            realExist: realExist ?? false,
            existStatus: existStatus ?? false
          });
        }
      }
    );
  };

  if (cached?.email) {
    if (!email) setEmail(cached.email);
    return (
      <Column>
        <div className="grow">
          <FlowHeader
            back={false}
            title={t("flow.id_control.label.login_title")}
            description={t("flow.id_control.label.select_an_account")}
            sub_description={subDescription}
          />
        </div>
        <div className="shrink-0">
          <div
            className={twMerge(
              "flex cursor-pointer flex-row items-center space-x-4 rounded-md bg-blue-50 p-4 hover:bg-blue-100 dark:bg-slate-800 dark:hover:bg-slate-700",
              origin === FromURL.Partner ? "bg-slate-100 hover:bg-slate-200" : "",
              isPending ? "pointer-events-none opacity-50" : ""
            )}
            onClick={() => execute()}
            data-cy="button-login-as-known-user"
          >
            <Avatar size={12} src={cached.avatar} fallback={cached.name || ""} />
            <div className="flex w-1 grow flex-col justify-center">
              <Base
                className={twMerge(
                  "w-full overflow-hidden text-ellipsis whitespace-nowrap font-semibold"
                )}
              >
                {cached.name}
              </Base>
              <Base className={twMerge("w-full overflow-hidden text-ellipsis whitespace-nowrap")}>
                {cached.email}
              </Base>
            </div>
            <Base>
              <ChevronRightIcon className="h-6 w-6" />
            </Base>
          </div>
        </div>
        <A
          className="mt-8 text-primary"
          onClick={() => {
            removeCached();
            setEmail("");
          }}
        >
          {t("flow.id_control.label.change_email")}
        </A>
      </Column>
    );
  }

  return (
    <>
      <Column>
        <div className="mb-2 grow sm:mb-0 2xl:mb-6">
          <FlowHeader
            back={false}
            title={t("flow.id_control.label.hello")}
            description={t("flow.id_control.label.hello_description")}
            sub_description={subDescription}
          />

          <InputLabel
            type="email"
            placeholder={t("flow.id_control.input.placeholder_email")}
            value={email}
            autoComplete="email"
            autoFocus
            onChange={(value: string) => setEmail(value)}
            label={t("flow.id_control.input.email_address")}
          />
        </div>
        <Button
          shortcut="enter"
          className="shrink-0"
          onClick={() => execute()}
          icon={<ChevronRightIcon />}
          loading={isPending}
        >
          {t("flow.id_control.input.continue")}
        </Button>
        <div className="mt-5 grow">
          <Base className={"mb-6 text-gray-500 text-xs dark:text-gray-500"}>
            {origin === FromURL.None
              ? t("flow.id_control.label.olkypass_service_kyc")
              : (getFooter ?? t("flow.id_control.label.olkypass_service_kyc"))}
          </Base>
          <Base className={"text-gray-500 text-xs dark:text-gray-500"}>
            {t("flow.id_control.label.politique_confidentiality")}
            <a
              href={config.OLKY_MAIN_LINK + language + config.OLKY_PRIVACY_POLICY_LINK}
              target="_blank"
              className={
                "cursor-pointer ps-1 font-semibold text-blue-500 capitalize hover:underline"
              }
            >
              {t("flow.id_control.actions.GDPR_accept_4")}
            </a>
          </Base>
        </div>
      </Column>
    </>
  );
}

export default AccountWelcome;
